import { settingsAPI } from '../services/api';
import createTenantCache from '../services/createTenantCache';
import { subscribe as subscribeToSettingsCache, BRANDING_SIGNAL } from '../services/settingsListCache';

/**
 * useBranding — fetch + cache the tenant's document branding.
 *
 * PR-36 §1.4 (2026-08-17). Extracts what were five duplicated in-component
 * useEffect fetches into a single hook with module-level caching and in-flight
 * dedupe. Same structural shape as `useCompanyProfile`.
 *
 * Consumers (BR-1):
 *   #1 BrandingPage         — authoring surface, uses settingsAPI directly (writer)
 *   #2 InvoiceReviewSend    — was PR-36 target
 *   #3 SentQuoteView        — was PR-36 target
 *   #4 QuoteReviewSend      — was PR-36 target
 *   #5 ChangeOrderReviewSend — was PR-36 target
 *   #6 (future) DashboardFirstRun — commented-out fetch; ready when it's needed
 *
 * ─── Vocabulary (§1.1) ─────────────────────────────────────────────────────
 *
 * The canonical spelling is SNAKE_CASE, matching what `BrandingPage` writes
 * and what `QuoteDocument.DocumentHeader` reads:
 *
 *   logo_url                    — the document logo (S3 URL)
 *   logo_meta                   — { width, height, contentType }
 *   primary_color               — brand accent
 *   secondary_color             — brand accent (unused by DocumentHeader today)
 *   text_color                  — body text (unused by DocumentHeader today)
 *   logo_position               — 'left' | 'center' | 'right'
 *   email_signature             — HTML block
 *   include_logo_in_signature   — boolean
 *   signature_logo_size         — 'sm' | 'md' | 'lg'
 *
 * The onboarding wizard writes camelCase (`brandColor`, `logo`, etc.) which
 * `DocumentHeader` cannot read. That divergence is Family A instance 13 and
 * gets resolved in PR-42 (not here) — see 320_ §1.1 for the rationale on
 * splitting the wizard fix out.
 *
 * 🛑 Do NOT add a fallback reader (`logo_url || logo`). That is the shape we
 *    have paid for twice. Snake_case is the winner because it has the working
 *    consumer; the loser (camelCase from the wizard) is fixed at the writer.
 */

// IDI-304: scoped to the signed-in tenant (services/tenantScope) and cleared
// on logout. This cache feeds CUSTOMER-FACING documents (QuoteDocument header:
// logo, brand colours, email signature) — serving the previous dealer's entry
// put another dealer's logo on a quote sent to a customer.
const cache = createTenantCache(() => settingsAPI.getBranding(), { label: 'useBranding' });

/** Force a re-fetch (e.g. after the user saves Settings → Branding). */
export function refreshBranding() {
  return cache.refresh();
}

// IDI-718: a save in Settings re-reads this copy (its own key only; the
// "everything" signal is covered by the tenant reset). Until the answer
// arrives, and if the re-read fails, the old value stays on screen.
subscribeToSettingsCache((key) => {
  if (key === BRANDING_SIGNAL) refreshBranding();
});

/** Reset the module-level cache. Test-only escape hatch (logout uses tenantScope). */
export function __resetBrandingCache() {
  cache.reset();
  cache.__clearSubscribers();
}

export default function useBranding() {
  const { value: branding, loading } = cache.useCached();

  return {
    branding: branding || {},
    logoUrl: branding?.logo_url || '',
    primaryColor: branding?.primary_color || '',
    logoPosition: branding?.logo_position || 'left',
    loading,
    refresh: refreshBranding,
  };
}
